import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trophy, Medal, Crown, Flame, TrendingUp, Award, BookOpen, ChevronRight, Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const CATS = [
  { id: "overall", label: "Overall" },
  { id: "weekly", label: "This Week" },
  { id: "monthly", label: "This Month" },
  { id: "labs", label: "Most Labs" },
  { id: "badges", label: "Most Badges" },
];
const PODIUM = { 1: { c: "#f4d675", ring: "ring-yellow-400/50", label: "1st" }, 2: { c: "#cbd5e1", ring: "ring-slate-300/50", label: "2nd" }, 3: { c: "#d8a26a", ring: "ring-amber-600/40", label: "3rd" } };

const initials = (n) => (n || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

function Avatar({ name, size = 40, color }) {
  return (
    <div className="rounded-full grid place-items-center font-bold text-background shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36, background: color || "hsl(var(--primary))" }}>
      {initials(name)}
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [cat, setCat] = useState("overall");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileId, setProfileId] = useState(null);
  const [profile, setProfile] = useState(null);

  const load = useCallback(async (c) => {
    setLoading(true);
    try { const { data } = await api.get(`/leaderboard?category=${c}`); setData(data); }
    catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(cat); }, [cat, load]);

  const openProfile = async (id) => {
    setProfileId(id); setProfile(null);
    try { const { data } = await api.get(`/leaderboard/profile/${id}`); setProfile(data); }
    catch (e) { toast.error(apiErr(e)); setProfileId(null); }
  };

  const entries = data?.entries || [];
  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);
  const me = data?.me;

  if (data && data.enabled === false) {
    return <div className="p-8 max-w-3xl mx-auto text-center text-muted-foreground" data-testid="leaderboard-disabled">The leaderboard is currently disabled by your administrator.</div>;
  }

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto" data-testid="leaderboard-page">
      <div className="flex items-center gap-3">
        <Trophy className="h-6 w-6 text-primary" />
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Leaderboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Climb the ranks by completing labs, earning badges, and mastering Linux.</p>
        </div>
      </div>

      {/* Your standing */}
      {me && (
        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="your-standing">
          <div className="text-xs uppercase tracking-widest text-primary font-semibold mb-3">Your Current Standing</div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Stat label="Rank" value={me.hidden ? "Hidden" : `#${me.rank} of ${me.total}`} icon={TrendingUp} />
            <Stat label="Score" value={me.score.toLocaleString()} icon={Trophy} />
            <Stat label="Labs" value={`${me.labs} / ${me.total_labs}`} icon={BookOpen} />
            <Stat label="Badges" value={me.badges} icon={Award} />
            <Stat label="Level" value={me.level_title} icon={Crown} />
          </div>
          <div className="mt-4">
            <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Overall progress</span><span>{me.progress_pct}%</span></div>
            <Progress value={me.progress_pct} className="h-2" />
          </div>
          {me.distance_to_next > 0 && me.next_rank && (
            <div className="mt-3 text-sm text-foreground" data-testid="distance-to-next">
              <Flame className="inline h-4 w-4 text-orange-500 mr-1" />
              You're <span className="font-bold text-primary">{me.distance_to_next.toLocaleString()}</span> points away from Rank #{me.next_rank}. Complete another lab to move up!
            </div>
          )}
          {me.rank === 1 && !me.hidden && <div className="mt-3 text-sm font-medium text-yellow-500" data-testid="top-rank-msg"><Crown className="inline h-4 w-4 mr-1" />You're #1 — the reigning Linux Champion. Keep it up!</div>}
        </div>
      )}

      <Tabs value={cat} onValueChange={setCat} className="mt-6">
        <TabsList className="flex-wrap h-auto">
          {CATS.map((c) => <TabsTrigger key={c.id} value={c.id} data-testid={`cat-${c.id}`}>{c.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="h-64 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
      ) : entries.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed p-12 text-center text-muted-foreground">No ranked students yet. Complete a lab to get on the board!</div>
      ) : (
        <>
          {/* Podium */}
          <div className="mt-6 grid grid-cols-3 gap-3 items-end" data-testid="podium">
            {[podium[1], podium[0], podium[2]].map((p, idx) => {
              if (!p) return <div key={idx} />;
              const meta = PODIUM[p.rank];
              const tall = p.rank === 1;
              return (
                <button key={p.user_id} onClick={() => openProfile(p.user_id)} data-testid={`podium-${p.rank}`}
                  className={`flex flex-col items-center rounded-xl border bg-card p-4 hover:border-primary/40 transition-all ${tall ? "pt-6" : "mt-6"} ${p.is_me ? "ring-2 ring-primary" : ""}`}>
                  <div className="relative">
                    <Avatar name={p.name} size={tall ? 64 : 52} color={meta.c} />
                    <Medal className="absolute -bottom-1 -right-1 h-6 w-6" style={{ color: meta.c }} fill={meta.c} />
                  </div>
                  <div className="font-display font-bold mt-3 text-center text-sm truncate max-w-full">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground">{p.level_title}</div>
                  <div className="font-mono font-bold text-lg mt-1" style={{ color: meta.c }}>{(p.period_score ?? p.score).toLocaleString()}</div>
                  <div className="text-[11px] text-muted-foreground">{p.labs} labs · {p.badges} badges</div>
                </button>
              );
            })}
          </div>

          {/* Full table */}
          <div className="mt-4 rounded-xl border border-border bg-card overflow-hidden">
            <div className="hidden sm:grid grid-cols-[48px_1fr_100px_80px_80px_140px] gap-2 px-4 py-2.5 bg-secondary/50 text-xs uppercase tracking-wider text-muted-foreground">
              <div>Rank</div><div>Student</div><div className="text-right">Score</div><div className="text-right">Labs</div><div className="text-right">Badges</div><div>Progress</div>
            </div>
            {rest.map((e) => (
              <button key={e.user_id} onClick={() => openProfile(e.user_id)} data-testid={`row-${e.rank}`}
                className={`w-full grid grid-cols-[48px_1fr_auto] sm:grid-cols-[48px_1fr_100px_80px_80px_140px] gap-2 items-center px-4 py-3 text-left border-t border-border hover:bg-accent/50 transition-colors ${e.is_me ? "bg-primary/10 hover:bg-primary/15" : ""}`}>
                <div className="font-mono font-bold text-muted-foreground">{e.rank}</div>
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={e.name} size={36} />
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2">{e.name}{e.is_me && <Badge variant="secondary" className="text-[10px]">You</Badge>}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.level_title}{e.streak > 1 && <span className="ml-2 text-orange-500"><Flame className="inline h-3 w-3" /> {e.streak}d</span>}</div>
                  </div>
                </div>
                <div className="text-right font-mono font-semibold sm:col-auto">{(e.period_score ?? e.score).toLocaleString()}</div>
                <div className="hidden sm:block text-right font-mono text-sm">{e.labs}</div>
                <div className="hidden sm:block text-right font-mono text-sm">{e.badges}</div>
                <div className="hidden sm:flex items-center gap-2"><Progress value={e.progress_pct} className="h-1.5" /><span className="text-xs text-muted-foreground w-8">{e.progress_pct}%</span></div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Public profile dialog */}
      <Dialog open={!!profileId} onOpenChange={(o) => !o && setProfileId(null)}>
        <DialogContent data-testid="profile-dialog">
          <DialogHeader><DialogTitle className="font-display">Achievement Profile</DialogTitle></DialogHeader>
          {!profile ? <div className="h-40 grid place-items-center"><Loader2 className="h-4 w-4 animate-spin" /></div> : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar name={profile.name} size={56} />
                <div>
                  <div className="font-display font-bold text-lg">{profile.name}</div>
                  <Badge variant="secondary">{profile.level_title}</Badge>
                </div>
                <div className="ml-auto text-right"><div className="font-mono font-bold text-xl text-primary">{profile.score.toLocaleString()}</div><div className="text-xs text-muted-foreground">points</div></div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{profile.labs_completed} of {profile.total_labs} labs</span><span>{profile.progress_pct}%</span></div>
                <Progress value={profile.progress_pct} className="h-2" />
              </div>
              {profile.milestones.length > 0 && (
                <div className="space-y-1">
                  {profile.milestones.map((mm, i) => <div key={i} className="text-sm flex items-center gap-2"><Trophy className="h-3.5 w-3.5 text-primary" />{mm}</div>)}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Badges earned ({profile.badges.length})</div>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-auto">
                  {profile.badges.length === 0 && <span className="text-sm text-muted-foreground">No badges yet.</span>}
                  {profile.badges.map((b) => <Badge key={b.lab_id} variant="outline" className="gap-1"><Award className="h-3 w-3 text-primary" />{b.title}</Badge>)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground flex items-center gap-1"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="font-display font-bold text-lg mt-0.5 truncate">{value}</div>
    </div>
  );
}
